const friendlyErrors = {
  CARBON_FINALIZATION_EVIDENCE_REQUIRED:
    "Link available supporting evidence to the activities listed above, then check readiness again.",
  CARBON_REVISION_NOT_FINALIZED:
    "Finalize this reporting revision before calculating it. Finalization locks the declared inputs so they cannot be changed after seeing the result.",
  CARBON_REVISION_ALREADY_CALCULATED:
    "This reporting revision has already been calculated. Create a correction revision if its declared inputs need to change.",
  CARBON_REVISION_CALCULATION_PARAMETERS_LOCKED:
    "This reporting revision already has a calculation with fixed emission factors. Retry that calculation or create a correction revision.",
  CARBON_CALCULATION_RETRY_LIMIT_REACHED:
    "This calculation could not complete after several attempts. Contact support to review the calculation log before trying again.",
  CARBON_REVISION_GRAPH_INCOMPLETE:
    "This reporting revision is not ready. Complete its locations, emission sources, and activity data, then try again.",
  CARBON_FACTOR_RELEASE_NOT_PUBLISHED:
    "No approved emission-factor set is available. Ask a Carbon administrator to publish one before calculating.",
  CARBON_CALCULATION_RUN_NOT_FOUND:
    "No completed calculation is available for this reporting revision yet.",
  CARBON_RESULT_NOT_FOUND:
    "The calculation has not produced a result yet. Wait for processing to finish and refresh the page.",
  UNEXPECTED_FATAL:
    "An internal error stopped this calculation. Please retry; contact support if it happens again.",
  VALIDATION_ERROR:
    "Some reporting data could not be validated. Review the activity data and evidence, then try again.",
  DBT_BUILD_FAILED:
    "The calculation service could not finish. Please retry; contact support if it happens again.",
  CALCULATION_TIMEOUT:
    "The calculation took too long. Please retry; contact support if it happens again.",
};

export const friendlyCarbonError = (error = {}) => {
  const code = error.code || error.message;
  if (friendlyErrors[code]) return friendlyErrors[code];
  if (/missing FROM-clause|carboncalculationrun__/i.test(error.message || "")) {
    return "The calculation could not be started because of an internal data error. Please retry after the system has been checked.";
  }
  return error.message || "Something went wrong. Please try again.";
};

export const carbonContextFromSearch = (search = "") => {
  const queryStart = search.indexOf("?");
  const params = new URLSearchParams(
    queryStart >= 0 ? search.slice(queryStart) : search,
  );
  return {
    inventoryId: params.get("inventoryId") || "",
    revisionId: params.get("revisionId") || "",
  };
};

export const withCarbonContext = (path, context = {}) => {
  const params = new URLSearchParams();
  if (context.inventoryId) params.set("inventoryId", context.inventoryId);
  if (context.revisionId) params.set("revisionId", context.revisionId);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const carbonResourcePath = (resource, id, action = "show", context) => {
  const base = id
    ? `/${resource}/${encodeURIComponent(id)}/${action}`
    : `/${resource}/${action}`;
  return withCarbonContext(base, context);
};

export const carbonWarningLabel = (code) => ({
  PROXY_FACTOR: "I understand that a proxy emission factor was used",
  FACTOR_PROXY_USED: "I understand that a proxy emission factor was used",
  MISSING_EVIDENCE: "I understand that some activity data has no supporting evidence",
}[code] || `I have reviewed this warning: ${String(code).replace(/_/g, " ").toLowerCase()}`);

export const reportingRevisionLabel = (revision) =>
  Number(revision?.revisionNumber) === 1
    ? "Initial reporting data"
    : Number(revision?.revisionNumber) > 1 ? `Revision ${Number(revision.revisionNumber) - 1}` : 'Reporting data';

export const reportingRevisionStatusLabel = (revision) => {
  const status = revision?.revisionStatus || revision?.status;
  const label = { draft: "Draft", finalized: "Finalized", superseded: "Superseded" }[status];
  return `${reportingRevisionLabel(revision)}${label ? ` · ${label}` : ""}`;
};

export const carbonFileSize = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return "Not recorded";
  return bytes < 1000 ? `${bytes} B` : bytes < 1000000
    ? `${(bytes / 1000).toFixed(1)} KB` : `${(bytes / 1000000).toFixed(1)} MB`;
};

export const reportingVersionLabel = (revision, reportingYear) =>
  revision
    ? `${reportingYear || revision.reportingYear || "Reporting year"} - ${reportingRevisionLabel(revision)}`
    : "Select reporting data";
